/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, StrictMode } from 'react';
import styled, { StyleSheetManager } from 'styled-components';
import { GridContainer, GridRow, GridItem, gridConfig, GlobalThemeComponent } from '@tvg/fuse-design';
import appWebhook from '@sd-utility/appwebhook';
import ImageCarousel from 'components/ImageCarousel/ImageCarousel';
import ProductDetails from 'components/ProductDetails/ProductDetails';
import CreditOffers from 'components/CreditOffers';
import DeliveryAndReturns from 'components/DeliveryAndReturns';
import StockMessage from 'components/StockMessage/StockMessage';
import AddToBasket from 'components/AddToBasket/AddToBasket';
import SaveForLater from 'components/SaveForLater/SaveForLater';
import ProductDescription from 'components/Description';
import RatingsAndReviews from 'components/RatingsAndReviews/RatingsAndReviews';
import NonReturnableInfo from 'components/NonReturnableInfo/NonReturnableInfo';
import PrfInfo from 'components/PrfInfo/PrfInfo';
import OptionSelectorList from 'components/OptionSelectorList/OptionSelectorList';
import SelectedSkuProvider from 'context/SelectedSku/SelectedSkuProvider';
import DynamicPrice from 'components/DynamicPrice';
import Recommendations from 'components/Recommendations/Recommendations';
import TrueFitWidget from 'components/TrueFitWidget';
import Personalization from 'components/Personalization/Personalization';
import LoadBee from 'components/LoadBee/LoadBee';
import InlineContent from 'components/OneWorldSync/InlineContent';
import EnergyLabel from 'components/OneWorldSync/EnergyLabel';
import TradeIn from 'components/TradeIn/TradeIn';
import { Insurance } from 'components/Insurance/Insurance';
import PercentageAndDeliveryVoucher from 'components/PromotionVoucher/PercentageAndDeliveryVoucher';
import InsuranceVoucher from 'components/PromotionVoucher/InsuranceVoucher';
import CustomPromotionComponent from 'components/PromotionVoucher/CustomPromotionVoucher';
import PromotionIdentifier from 'components/PromotionIdentifier/PromotionIdentifier';
import { DialogService } from 'components/DialogService/DialogService';
import { Addon } from 'components/Addon/Addon';
import AppleRichContent from 'components/AppleRichContent/AppleRichContent';
import { CustomPromotionVoucher, PromotionVoucher } from 'types/Product';
import { VoucherTypes } from 'constants/promotion-voucher-constants';
import BrandTypes from 'constants/brand';
import WeeInfo from 'components/WeeInfo/WeeInfo';
import ShareButton from 'components/ShareButton/ShareButton';
import sanitizeProductName from 'utilities/product/sanitizeProductName';
import getSessionConfirmationNumber from 'services/session/getSessionConfirmationNumber';
import CsrfTokenError from 'utilities/logging/error-log-type/csrfTokenError';
// import { captureClientSideError } from 'utilities/util';
import LoggerFactory from 'utilities/loggerv2/loggerFactory';
import ErrorCodes from 'utilities/loggerv2/errorCodes';
import { SiteContext } from 'context/SiteContext';
import { ContainerProps } from './ContainerProps';

const ProductPageContainer = styled.div<Partial<ContainerProps>>`
  font-size: var(--font-size-m1);
  line-height: var(--font-line-height-m1);
`;

const StyledGridItem = styled(GridItem)`
  display: flex;
  flex-direction: column-reverse;

  @media (min-width: ${gridConfig.m.minWidth}) {
    flex-direction: column;
  }
`;

const AccordionsGridItem = styled(GridItem)`
  display: flex;
  flex-direction: column-reverse;

  @media (min-width: ${gridConfig.m.minWidth}) {
    flex-direction: column;
  }
`;

const ProductDetailsContainer = styled.div`
  @media (min-width: ${gridConfig.m.minWidth}) {
    padding-left: var(--spacing-space16);
  }

  @media (min-width: ${gridConfig.l.minWidth}) {
    padding-left: var(--spacing-space24);
  }
`;

const SaveAndShareContainer = styled.div`
  display: flex;
  justify-content: center;
`;
const logger = LoggerFactory.getLogger('Container');

export default function Container(props: ContainerProps) {
  const {
    productId,
    identification,
    images,
    brand,
    name,
    price,
    lowestPrice,
    reviews,
    skus,
    description,
    BV_URL,
    returnable,
    features,
    trueFitEnabled,
    personalizableMaxLength,
    isInsuranceAvailable,
    promotionIdentifier,
    promotionVoucher,
    digitalDownload,
    insuranceContent,
    roundel,
    linkedAddonServicesContent,
    tradeInValue,
    videos,
    appleRichContentKey,
    tradingDepartmentId,
    site,
    weeeEligibilityInd,
    vdpMockEnabled,
    creditOffersMockEnabled,
    hasDeliveryContent,
  } = props;
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [sessionConfirmationNumber, setSessionConfirmationNumber] = useState<string>('');
  const [canShare, setCanShare] = useState<any | undefined>(true);
  const [platform, setPlatform] = useState('');

  const productName = brand ? `${brand} ${name}` : name;

  useEffect(() => {
    const { userAgent } = window.navigator;
    const isAppUserAgent = /SDAndroidApp|SDIPhoneApp_2\.0|SDIPhoneApp/.test(userAgent);
    if (userAgent.includes('SDIphoneApp') || userAgent.includes('SDIPhoneApp_2.0')) {
      setPlatform('iphone');
    } else if (userAgent.includes('SDAndroidApp')) {
      setPlatform('android');
    } else {
      setPlatform('web');
    }
    if (window.navigator.share === undefined && !isAppUserAgent) {
      setCanShare(false);
    }
  }, []);

  const mpn = skus[0].manufacturerPartNumber;

  const handleGetSessionConfirmationNumber = async () => {
    try {
      const response = await getSessionConfirmationNumber(window);

      if (response && response.sessionConfirmationNumber) {
        setSessionConfirmationNumber(response.sessionConfirmationNumber);
      }
    } catch (error) {
      (await logger).logWithCode(ErrorCodes.CSRF_TOKEN_ERROR, error);
    }
  };

  const handleSetCsrfToken = async () => {
    try {
      const csrfTokenMetaTagValue = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

      if (!csrfTokenMetaTagValue) {
        throw new CsrfTokenError('CSRF token is missing');
      }

      setCsrfToken(csrfTokenMetaTagValue);
    } catch (error) {
      (await logger).logWithCode(ErrorCodes.CSRF_TOKEN_ERROR, error as Error);
    }
  };

  const handleProductView = () => {
    appWebhook('productView', {
      currencyCode: price.amount.currencyCode,
      productData: [
        {
          SKU: skus[0].option,
          catalogueID: identification.itemNumber,
          name: productName,
          quantity: 1,
          unitPrice: price.amount.decimal,
        },
      ],
    });
  };

  const stopLoadingOnApp = () => {
    appWebhook('stopLoading', true);
  };

  useEffect(() => {
    stopLoadingOnApp();
    handleSetCsrfToken();
    handleGetSessionConfirmationNumber();
    handleProductView();
  }, []);

  const isPromotionVoucher = (voucher: any): voucher is PromotionVoucher => voucher && voucher.type !== undefined;

  const isCustomPromotionVoucher = (voucher: any): voucher is CustomPromotionVoucher =>
    voucher && voucher.dialogDescription !== undefined;

  const processPromotionType = (voucher: any): PromotionVoucher | CustomPromotionVoucher | undefined => {
    if (isPromotionVoucher(voucher)) {
      return voucher;
    }
    if (isCustomPromotionVoucher(voucher)) {
      return voucher;
    }
    return undefined;
  };
  const processedPromotionVoucher = processPromotionType(promotionVoucher);

  return (
    <StrictMode>
      <StyleSheetManager enableVendorPrefixes>
        <GlobalThemeComponent brand={site} />
        <SiteContext.Provider value={site}>
          <SelectedSkuProvider
            skus={skus}
            isInsuranceAvailable={isInsuranceAvailable}
            csrfToken={csrfToken}
            sessionConfirmationNumber={sessionConfirmationNumber}
            productId={productId}
            productItemNumber={identification.itemNumber}
            productName={productName}
            productPrice={price.amount}
            personalizableMaxLength={personalizableMaxLength}
            digitalDownload={digitalDownload}
            insuranceContent={insuranceContent}
            linkedAddonServicesContent={linkedAddonServicesContent}
          >
            <ProductPageContainer id="product-page-container" data-testid="product">
              <GridContainer>
                <GridRow>
                  <StyledGridItem data-testid="carousel-promo" m={6} l={7}>
                    {promotionIdentifier && (
                      <PromotionIdentifier
                        text={promotionIdentifier.displayName}
                        color={promotionIdentifier.colour}
                        size="m"
                      />
                    )}
                    <ImageCarousel
                      images={images}
                      productTitle={productName}
                      roundel={roundel}
                      videos={videos}
                      site={site}
                    />
                  </StyledGridItem>

                  <StyledGridItem m={6} l={5}>
                    <ProductDetailsContainer>
                      <ProductDetails
                        brand={brand}
                        name={name}
                        price={price.amount}
                        reviewTotal={reviews.total}
                        reviewStars={reviews.stars}
                        productId={productId}
                      />

                      <EnergyLabel brand={brand} ean={skus[0].ean} mpn={mpn} option={skus[0].option} site={site} />

                      {!processedPromotionVoucher && site !== BrandTypes.VERYIE && (
                        <CreditOffers
                          itemNumber={identification.itemNumber}
                          productName={productName}
                          productPrice={lowestPrice.amount.current}
                          productPriceDecimal={lowestPrice.amount.decimal}
                          site={site}
                          creditOffersMockEnabled={creditOffersMockEnabled}
                          skus={skus}
                        />
                      )}

                      {processedPromotionVoucher &&
                        (isPromotionVoucher(processedPromotionVoucher) &&
                        processedPromotionVoucher.type === VoucherTypes.Insurance ? (
                          <InsuranceVoucher promotionVoucher={processedPromotionVoucher} />
                        ) : (
                          <PercentageAndDeliveryVoucher promotionVoucher={processedPromotionVoucher} />
                        ))}

                      {processedPromotionVoucher && isCustomPromotionVoucher(processedPromotionVoucher) && (
                        <CustomPromotionComponent promotionVoucher={processedPromotionVoucher} />
                      )}

                      {hasDeliveryContent && !digitalDownload && (
                        <DeliveryAndReturns
                          brand={brand}
                          site={site}
                          skus={skus}
                          csrfToken={csrfToken}
                          vdpMockEnabled={vdpMockEnabled}
                        />
                      )}

                      {returnable && <NonReturnableInfo returnable={returnable} />}

                      {features?.length && skus && skus.length > 1 ? (
                        <OptionSelectorList features={features} skus={skus} />
                      ) : null}

                      {trueFitEnabled && (
                        <TrueFitWidget
                          locale={site === 'veryie' ? 'en_IE' : 'en_GB'}
                          itemNumber={identification.itemNumber}
                          colour={skus[0]?.options?.colour}
                        />
                      )}
                      {isInsuranceAvailable && <Insurance />}
                      <Addon />

                      {personalizableMaxLength && <Personalization maxLength={personalizableMaxLength} />}
                      <StockMessage />

                      <PrfInfo prfValue={price.amount.prfValue} weeeEligibilityInd={weeeEligibilityInd} />

                      <DynamicPrice price={price.amount} />
                      <AddToBasket />
                      {(canShare || site !== BrandTypes.VERYIE) && (
                        <SaveAndShareContainer>
                          {site !== BrandTypes.VERYIE && (
                            <SaveForLater
                              sku={skus[0]}
                              csrfToken={csrfToken}
                              sessionConfirmationNumber={sessionConfirmationNumber}
                              productId={productId}
                              itemNumber={identification.itemNumber}
                            />
                          )}
                          {canShare && (
                            <ShareButton
                              productName={sanitizeProductName(productName)}
                              site={site}
                              image={images[0]?.identifier}
                              platform={platform}
                            />
                          )}
                        </SaveAndShareContainer>
                      )}
                      {tradeInValue && site !== BrandTypes.VERYIE && <TradeIn tradeInValue={tradeInValue} />}
                    </ProductDetailsContainer>
                  </StyledGridItem>
                </GridRow>

                {tradingDepartmentId && (mpn || skus[0]?.ean) ? (
                  <GridRow>
                    <AccordionsGridItem>
                      <InlineContent
                        brand={brand}
                        ean={skus[0].ean}
                        mpn={mpn}
                        option={skus[0].option}
                        Description={
                          <ProductDescription
                            itemNumber={identification.itemNumber}
                            ean={identification.ean}
                            description={description}
                            useAccordion
                          />
                        }
                        AppleContent={
                          appleRichContentKey ? <AppleRichContent contentKey={appleRichContentKey} /> : undefined
                        }
                        LoadBee={<LoadBee ean={identification.ean} />}
                      />
                    </AccordionsGridItem>
                  </GridRow>
                ) : (
                  <GridRow>
                    <StyledGridItem>
                      <ProductDescription
                        itemNumber={identification.itemNumber}
                        ean={identification.ean}
                        description={description}
                      />
                    </StyledGridItem>

                    {appleRichContentKey && (
                      <StyledGridItem>
                        <AppleRichContent contentKey={appleRichContentKey} />
                      </StyledGridItem>
                    )}

                    <StyledGridItem>
                      <LoadBee ean={identification.ean} />
                    </StyledGridItem>

                    <StyledGridItem>
                      <InlineContent brand={brand} ean={skus[0].ean} mpn={mpn} option={skus[0].option} />
                    </StyledGridItem>
                  </GridRow>
                )}

                <GridRow>
                  <StyledGridItem>
                    <Recommendations hideBorder={!!tradingDepartmentId} />
                  </StyledGridItem>
                </GridRow>

                <GridRow>
                  <StyledGridItem>
                    <RatingsAndReviews productId={productId} BV_URL={BV_URL} />
                  </StyledGridItem>
                </GridRow>
                {weeeEligibilityInd && (
                  <GridRow>
                    <StyledGridItem>
                      <WeeInfo />
                    </StyledGridItem>
                  </GridRow>
                )}
              </GridContainer>
              <DialogService />
            </ProductPageContainer>
          </SelectedSkuProvider>
        </SiteContext.Provider>
      </StyleSheetManager>
    </StrictMode>
  );
}
